import React, { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Wallet, Receipt, Trash2, FileText, Upload, Loader2, Printer, ScanLine, Mail, RefreshCw, Package } from "lucide-react";
import { toast } from "sonner";
import { api, money2, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const STATUS_VARIANT = { paid: "default", sent: "secondary", draft: "outline", overdue: "destructive" };
const CATEGORIES = ["Software", "Travel", "Meals", "Office", "Marketing", "Utilities", "Other"];

export default function Finance() {
  const [params] = useSearchParams();
  const tab = params.get("tab") === "expenses" ? "expenses" : "invoices";
  return (
    <div>
      <PageHeader title="Invoicing & Expenses" subtitle="Invoices, expenses, and AI receipt scanning." testId="finance-header" />
      <Tabs defaultValue={tab}>
        <TabsList className="mb-6" data-testid="finance-tabs">
          <TabsTrigger value="invoices"><FileText className="mr-1.5 h-4 w-4" /> Invoices</TabsTrigger>
          <TabsTrigger value="expenses"><Receipt className="mr-1.5 h-4 w-4" /> Expenses</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices"><Invoices /></TabsContent>
        <TabsContent value="expenses"><Expenses /></TabsContent>
      </Tabs>
    </div>
  );
}

function Invoices() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", items: [{ description: "", qty: 1, price: 0 }], tax: 0, notes: "", status: "draft", recurring: false, frequency: "monthly" });
  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: products = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });

  const total = form.items.reduce((s, i) => s + Number(i.qty) * Number(i.price), 0) + Number(form.tax);
  const setItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", qty: 1, price: 0 }] }));
  const addProduct = (p) => setForm((f) => ({ ...f, items: [...f.items, { description: p.name, qty: 1, price: p.price || 0, product_id: p.id }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const create = async () => {
    try {
      await api.post("/invoices", { ...form, tax: Number(form.tax), items: form.items.map((i) => ({ ...i, qty: Number(i.qty), price: Number(i.price) })) });
      toast.success("Invoice created");
      setOpen(false); setForm({ client_name: "", client_email: "", items: [{ description: "", qty: 1, price: 0 }], tax: 0, notes: "", status: "draft", recurring: false, frequency: "monthly" });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    await api.patch(`/invoices/${id}/status?status=${status}`);
    toast.success(`Invoice ${status}`);
    qc.invalidateQueries({ queryKey: ["invoices"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const remove = async (id) => { await api.delete(`/invoices/${id}`); qc.invalidateQueries({ queryKey: ["invoices"] }); };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <div className="mb-5 flex justify-end"><Button onClick={() => setOpen(true)} data-testid="create-invoice-button"><Plus className="mr-2 h-4 w-4" /> New invoice</Button></div>
      {invoices.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices yet" description="Create and send your first professional invoice." action={<Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> New invoice</Button>} testId="invoices-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Due</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((inv) => (
                <tr key={inv.id} className="bg-card" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-semibold">{inv.number}{inv.recurring && <Badge variant="outline" className="ml-1.5 gap-1 border-accent/40 align-middle text-[10px] capitalize text-accent" data-testid={`invoice-recurring-${inv.id}`}><RefreshCw className="h-2.5 w-2.5" />{inv.frequency || "monthly"}</Badge>}</td>
                  <td className="px-4 py-3">{inv.client_name}</td>
                  <td className="px-4 py-3 font-medium">{money2(inv.total)}</td>
                  <td className="px-4 py-3"><Badge variant={STATUS_VARIANT[inv.status]} className="capitalize">{inv.status}</Badge></td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.due_date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreview(inv)} data-testid={`view-invoice-${inv.id}`}>View</Button>
                      {inv.status !== "paid" && (
                        <Select value={inv.status} onValueChange={(v) => setStatus(inv.id, v)}>
                          <SelectTrigger className="h-8 w-[100px] text-xs" data-testid={`invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{["draft", "sent", "paid"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(inv.id)} data-testid={`delete-invoice-${inv.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>Create invoice</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Client name</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="invoice-client-input" /></div>
              <div className="space-y-1.5"><Label>Client email</Label><Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="invoice-email-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Line items</Label>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <Input className="col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} />
                  <Input className="col-span-2" type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} data-testid={`item-qty-${idx}`} />
                  <Input className="col-span-4" type="number" placeholder="Price" value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} data-testid={`item-price-${idx}`} />
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-line-item"><Plus className="mr-1.5 h-3.5 w-3.5" /> Add line</Button>
                {products.length > 0 && (
                  <Select value="" onValueChange={(id) => { const p = products.find((x) => x.id === id); if (p) addProduct(p); }}>
                    <SelectTrigger className="h-9 w-[220px]" data-testid="invoice-product-select"><SelectValue placeholder="Add from inventory" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`invoice-product-option-${p.id}`}>{p.name}{p.price ? ` — ${money2(p.price)}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tax ($)</Label><Input type="number" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} data-testid="invoice-tax-input" /></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft", "sent", "paid"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Billing</Label>
                <Select value={form.recurring ? "recurring" : "one_time"} onValueChange={(v) => setForm({ ...form, recurring: v === "recurring" })}>
                  <SelectTrigger data-testid="invoice-recurring-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="one_time">One-time</SelectItem><SelectItem value="recurring">Recurring</SelectItem></SelectContent>
                </Select>
              </div>
              {form.recurring && (
                <div className="space-y-1.5"><Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="invoice-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["weekly", "monthly", "yearly"].map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="invoice-notes-input" /></div>
            <p className="text-right font-display text-lg font-bold">Total: {money2(total)}</p>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.client_name} data-testid="save-invoice-button">Create invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {preview && <InvoicePreview invoice={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function InvoicePreview({ invoice, onClose }) {
  const { user } = useAuth();

  const printInvoice = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const biz = esc(user?.business?.name || "Your Business");
    const rows = invoice.items
      .map(
        (it) => `<tr>
          <td>${esc(it.description)}</td>
          <td class="c">${esc(it.qty)}</td>
          <td class="r">${money2(it.price)}</td>
          <td class="r">${money2(it.qty * it.price)}</td>
        </tr>`
      )
      .join("");
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Invoice ${esc(invoice.number)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { background: #ffffff; color: #000000; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.5; padding: 48px; color: #000; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
  .biz { font-size: 22px; font-weight: 700; }
  .num { font-size: 13px; margin-top: 2px; }
  h1 { font-size: 26px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
  .meta { display: flex; justify-content: space-between; margin-bottom: 32px; }
  .label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700; margin-bottom: 4px; }
  .meta .r { text-align: right; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #000; padding: 8px 6px; }
  td { padding: 10px 6px; border-bottom: 1px solid #cccccc; }
  th.c, td.c { text-align: center; }
  th.r, td.r { text-align: right; }
  .totals { width: 260px; margin-left: auto; }
  .totals .line { display: flex; justify-content: space-between; padding: 6px 0; }
  .totals .grand { border-top: 2px solid #000; margin-top: 4px; padding-top: 10px; font-size: 16px; font-weight: 700; }
  .notes { margin-top: 40px; padding-top: 16px; border-top: 1px solid #cccccc; font-size: 12px; }
  .foot { margin-top: 48px; text-align: center; font-size: 11px; color: #555; }
  @media print { body { padding: 24px; } }
</style></head>
<body>
  <div class="head">
    <div><div class="biz">${biz}</div><div class="num">Invoice ${esc(invoice.number)}</div></div>
    <h1>Invoice</h1>
  </div>
  <div class="meta">
    <div><div class="label">Bill to</div><div>${esc(invoice.client_name)}</div><div>${esc(invoice.client_email || "")}</div></div>
    <div class="r"><div class="label">Issued</div><div>${esc(invoice.issue_date)}</div><div class="label" style="margin-top:8px">Due</div><div>${esc(invoice.due_date)}</div></div>
  </div>
  <table>
    <thead><tr><th>Item</th><th class="c">Qty</th><th class="r">Price</th><th class="r">Amount</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals">
    <div class="line"><span>Subtotal</span><span>${money2(invoice.subtotal)}</span></div>
    <div class="line"><span>Tax</span><span>${money2(invoice.tax)}</span></div>
    <div class="line grand"><span>Total</span><span>${money2(invoice.total)}</span></div>
  </div>
  ${invoice.notes ? `<div class="notes">${esc(invoice.notes)}</div>` : ""}
  <div class="foot">Thank you for your business.</div>
</body></html>`;

    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Please allow pop-ups for this site to download the invoice."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  const emailInvoice = () => {
    const bizName = user?.business?.name || "us";
    const lines = invoice.items.map((it) => `  • ${it.description} (x${it.qty}) — ${money2(it.qty * it.price)}`).join("\n");
    const subject = `Invoice ${invoice.number} from ${bizName}`;
    const body =
      `Hi ${invoice.client_name || "there"},\n\n` +
      `Please find your invoice ${invoice.number} below.\n\n` +
      `${lines}\n\n` +
      `Subtotal: ${money2(invoice.subtotal)}\n` +
      `Tax: ${money2(invoice.tax)}\n` +
      `Total due: ${money2(invoice.total)}\n` +
      `Due date: ${invoice.due_date}\n\n` +
      `${invoice.notes ? invoice.notes + "\n\n" : ""}` +
      `Thank you,\n${user?.name || ""}${bizName !== "us" ? `\n${bizName}` : ""}`;
    window.location.href = `mailto:${invoice.client_email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="invoice-preview-dialog">
        <div id="invoice-print" className="space-y-5 p-2">
          <div className="flex items-start justify-between">
            <div><p className="font-display text-2xl font-bold">{user?.business?.name || "Your Business"}</p><p className="text-sm text-muted-foreground">Invoice {invoice.number}</p></div>
            <Badge variant={STATUS_VARIANT[invoice.status]} className="capitalize">{invoice.status}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs uppercase text-muted-foreground">Bill to</p><p className="font-medium">{invoice.client_name}</p><p className="text-muted-foreground">{invoice.client_email}</p></div>
            <div className="text-right"><p className="text-xs uppercase text-muted-foreground">Issued / Due</p><p>{invoice.issue_date}</p><p className="text-muted-foreground">{invoice.due_date}</p></div>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground"><tr><th className="py-2">Item</th><th className="py-2 text-center">Qty</th><th className="py-2 text-right">Price</th><th className="py-2 text-right">Amount</th></tr></thead>
            <tbody>
              {invoice.items.map((it, i) => (
                <tr key={i} className="border-b border-border/60"><td className="py-2">{it.description}</td><td className="py-2 text-center">{it.qty}</td><td className="py-2 text-right">{money2(it.price)}</td><td className="py-2 text-right">{money2(it.qty * it.price)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="ml-auto w-48 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money2(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{money2(invoice.tax)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-display text-base font-bold"><span>Total</span><span>{money2(invoice.total)}</span></div>
          </div>
          {invoice.notes && <p className="text-sm text-muted-foreground">{invoice.notes}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={printInvoice} data-testid="print-invoice-button"><Printer className="mr-2 h-4 w-4" /> Download / Print PDF</Button>
          <Button onClick={emailInvoice} data-testid="email-invoice-button"><Mail className="mr-2 h-4 w-4" /> Email invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Expenses() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({ vendor: "", amount: 0, category: "Other", date: "", notes: "", receipt_file_id: null, expense_type: "one_time", frequency: "monthly" });
  const { data: expenses = [], isLoading } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });

  const scan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/receipts/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = res.data.extracted;
      setForm({ vendor: ex.vendor || "", amount: ex.amount || 0, category: CATEGORIES.includes(ex.category) ? ex.category : "Other", date: ex.date || "", notes: ex.notes || "", receipt_file_id: res.data.file_id, expense_type: "one_time", frequency: "monthly" });
      setOpen(true);
      toast.success("Receipt scanned — review the details.");
      refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setScanning(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const create = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense logged");
      setOpen(false); setForm({ vendor: "", amount: 0, category: "Other", date: "", notes: "", receipt_file_id: null, expense_type: "one_time", frequency: "monthly" });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/expenses/${id}`); qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const viewReceipt = async (fileId) => {
    const tab = window.open("", "_blank", "noopener");
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location = url; else window.open(url, "_blank", "noopener");
    } catch { if (tab) tab.close(); toast.error("Couldn't load that receipt."); }
  };

  if (isLoading) return <PageLoader />;
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const MONTHLY_FACTOR = { weekly: 52 / 12, monthly: 1, yearly: 1 / 12 };
  const monthlyRecurring = expenses
    .filter((e) => e.expense_type === "recurring")
    .reduce((s, e) => s + e.amount * (MONTHLY_FACTOR[e.frequency] || 1), 0);

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Total logged: <span className="font-display text-lg font-bold text-foreground">{money2(total)}</span>{monthlyRecurring > 0 && <span className="ml-3 inline-flex items-center gap-1 text-accent"><RefreshCw className="h-3.5 w-3.5" /> {money2(monthlyRecurring)}/mo recurring</span>}</p>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={scan} data-testid="receipt-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="scan-receipt-button">
            {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanLine className="mr-2 h-4 w-4" />} Scan receipt
          </Button>
          <Button onClick={() => { setForm({ vendor: "", amount: 0, category: "Other", date: "", notes: "", receipt_file_id: null, expense_type: "one_time", frequency: "monthly" }); setOpen(true); }} data-testid="add-expense-button"><Plus className="mr-2 h-4 w-4" /> Add expense</Button>
        </div>
      </div>

      {expenses.length === 0 ? (
        <EmptyState icon={Receipt} title="No expenses yet" description="Add expenses manually or scan a receipt with AI." action={<Button onClick={() => fileRef.current?.click()}><Upload className="mr-2 h-4 w-4" /> Scan a receipt</Button>} testId="expenses-empty" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">Vendor</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-border">
              {expenses.map((ex) => (
                <tr key={ex.id} className="bg-card" data-testid={`expense-row-${ex.id}`}>
                  <td className="px-4 py-3 font-medium">
                    {ex.vendor}
                    {ex.receipt_file_id && (
                      <button type="button" onClick={() => viewReceipt(ex.receipt_file_id)} className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-accent transition-opacity hover:opacity-70" title="View receipt" data-testid={`view-receipt-${ex.id}`}>
                        <ScanLine className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3"><Badge variant="secondary">{ex.category}</Badge></td>
                  <td className="px-4 py-3" data-testid={`expense-type-${ex.id}`}>
                    {ex.expense_type === "recurring"
                      ? <Badge variant="outline" className="gap-1 border-accent/40 capitalize text-accent"><RefreshCw className="h-3 w-3" />{ex.frequency || "monthly"}</Badge>
                      : <span className="text-xs text-muted-foreground">One-time</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{ex.date}</td>
                  <td className="px-4 py-3 font-medium">{money2(ex.amount)}{ex.expense_type === "recurring" && <span className="text-xs font-normal text-muted-foreground">/{({ weekly: "wk", monthly: "mo", yearly: "yr" })[ex.frequency] || "mo"}</span>}</td>
                  <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => remove(ex.id)} data-testid={`delete-expense-${ex.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{form.receipt_file_id ? "Review scanned expense" : "Add expense"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount ($)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.expense_type} onValueChange={(v) => setForm({ ...form, expense_type: v })}>
                  <SelectTrigger data-testid="expense-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="one_time">One-time / fixed</SelectItem><SelectItem value="recurring">Recurring</SelectItem></SelectContent>
                </Select>
              </div>
              {form.expense_type === "recurring" && (
                <div className="space-y-1.5"><Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="expense-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["weekly", "monthly", "yearly"].map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="expense-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.vendor} data-testid="save-expense-button">Save expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
